import { type BookkeepingManagerStore } from '@/features/admin/bookkeeping/BookkeepingManagerStore'
import { BookkeepingPendingAction } from '@/features/admin/bookkeeping/BookkeepingPendingAction'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import { Subject } from 'rxjs'
import { ModalViewModel } from '@/infra/ModalViewModel'

export class BookkeepingManagerDetailsViewModel {
  readonly modal = new ModalViewModel({
    onClose: () =>
      this.setBookkeepingPendingAction(BookkeepingPendingAction.none),
  })

  /**
   * Subject used to emit when details is updated.
   */
  private readonly _update = new Subject<BookkeepingPendingAction>()

  /**
   * Emits the bookkeeping pending action when details is updated.
   */
  readonly update$ = this._update.asObservable()

  /**
   * The bookkeeping pending action.
   */
  @observable
  accessor bookkeepingPendingAction = BookkeepingPendingAction.none

  constructor(
    private readonly _bookkeepingManagerStore: BookkeepingManagerStore,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _closeNav: () => void,
  ) {}

  /**
   * Whether the pending action can be resolved by confirming platform access.
   */
  @computed
  get showConfirmAccess() {
    return this.bookkeepingPendingAction.action === 'ConfirmAccess'
  }

  /**
   * Sets the bookkeeping pending action.
   *
   * @param bookkeepingPendingAction
   */
  @action.bound
  setBookkeepingPendingAction(
    bookkeepingPendingAction: BookkeepingPendingAction,
  ) {
    this.bookkeepingPendingAction = bookkeepingPendingAction
  }

  /**
   * Shows the bookkeeping pending action details.
   *
   * @param bookkeepingPendingAction
   */
  @action.bound
  show(bookkeepingPendingAction: BookkeepingPendingAction) {
    this.modal.show()
    this._closeNav()
    this.setBookkeepingPendingAction(bookkeepingPendingAction)
  }

  /**
   * Confirms the bookkeeping pending action platform access.
   */
  confirmAccess = task.resolved(async () => {
    await this._bookkeepingManagerStore
      .confirmManagedPlatformAccess(this.bookkeepingPendingAction.id)
      .catch(this._createAlertErrorHandler('Confirming access failed'))

    this._update.next(this.bookkeepingPendingAction)
    this._newAlert()
      .withState('success')
      .saying('You confirmed bookkeeping access.')
      .autoDismiss()
      .show()
    this.modal.close()
  })
}
